import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { claimCoupon } from "@/api/client";

const STORAGE_KEY = "biz-exit-intent-shown";
const DISCOUNT_BRL = 20;

function shouldShow() {
  if (typeof window === "undefined") return false;
  // Don't show inside admin or checkout
  const path = window.location.pathname;
  if (path.startsWith("/admin") || path.startsWith("/checkout")) return false;
  // Honor "already shown this week" flag
  try {
    const last = localStorage.getItem(STORAGE_KEY);
    if (!last) return true;
    const ts = Number(last);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - ts > sevenDays;
  } catch {
    return true;
  }
}

function markShown() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage may be unavailable — fail silently
  }
}

/**
 * Shows a last-chance coupon when the user attempts to leave the site.
 * Desktop: mouse leaves through the top of the viewport.
 * Mobile: fires after ~40s of scroll + inactivity.
 * Respects a 7-day cooldown via localStorage.
 */
export const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  useEffect(() => {
    if (!shouldShow()) return;

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      setOpen(true);
      markShown();
    };

    // Desktop: mouseleave from top
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    // Mobile fallback: fires 40s after first user interaction
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    const onScrollOrTouch = () => {
      if (mobileTimer || fired) return;
      mobileTimer = setTimeout(trigger, 40_000);
    };
    window.addEventListener("scroll", onScrollOrTouch, { once: true, passive: true });
    window.addEventListener("touchstart", onScrollOrTouch, { once: true, passive: true });

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScrollOrTouch);
      window.removeEventListener("touchstart", onScrollOrTouch);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await claimCoupon(email.trim(), "exit-intent-coupon");
      setCouponCode(result.code);
      setDone(true);
      toast.success("Cupom enviado pro seu e-mail! 🎉");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao gerar cupom");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      toast.success("Código copiado!");
    } catch {
      toast.error("Copia manual: " + couponCode);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <DialogTitle className="sr-only">Cupom de desconto antes de sair</DialogTitle>
        <div className="relative bg-gradient-brand px-6 py-8 text-white">
          <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-warning via-white to-warning opacity-80" />
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/80">Última chance</p>
              <h2 className="text-2xl font-extrabold leading-tight mt-1">
                Espera! Leva R$ {DISCOUNT_BRL} de desconto antes de sair
              </h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-sm text-muted-foreground">
            Deixe seu e-mail e liberamos um cupom de <strong className="text-foreground">R$ {DISCOUNT_BRL} de desconto</strong> no seu próximo certificado digital. Válido por 72h.
          </p>

          {done ? (
            <div className="space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-success/40 bg-success/5 p-5 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-success">Seu cupom</p>
                <p className="mt-2 font-mono text-3xl font-extrabold text-foreground tracking-wider">
                  {couponCode}
                </p>
                <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" /> Expira em 72 horas
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={copyCoupon}>
                  Copiar código
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    setOpen(false);
                    window.location.href = "/produtos";
                  }}
                >
                  Ver produtos <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                required
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full bg-gradient-brand text-white font-bold shadow-md hover:shadow-lg"
                size="lg"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Quero meu cupom de R$ {DISCOUNT_BRL}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                Sem spam. Usamos seu e-mail só para enviar o cupom e lembretes de renovação.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
